// شمارشِ خامِ کرالر → گره‌ها و جریان‌های سنکی.
//
// این فایل **تنها جایی است** که معنیِ خطِ لوله به شکلِ نمودار ترجمه می‌شود.
// تاکسونومی اختراع نشده: نامِ پله‌ها از `event_crawler/ARCHITECTURE-VNEXT.md`
// می‌آید و برچسبِ هر جریان همان اسمِ مرحله است، تا وقتی چیزی عجیب دیدی
// بتوانی همان‌جا را در آن سند پیدا کنی.
//
// ⚠ **بازنویسیِ ۲۰۲۶-۰۸-۲۰.** نسخه‌ی قبل چرخه‌ی عمرِ نسلِ گذشته را می‌کشید
// (`S…/T…`ِ LIFECYCLE.md) و سه جدولش دیگر وجود ندارد. آن شماره‌ها هنوز برای
// **سمتِ اپ** معتبرند، ولی زنجیره‌ی بالادستی عوض شده و این‌جا همان تازه کشیده
// می‌شود.
//
// خالص و بی‌ایمپورت، پس با `npm test` آزمودنی است — و آزمودنش مهم است، چون
// نمودارِ اشتباه بدتر از نبودِ نمودار است: به‌جای «نمی‌دانم»، «می‌دانم» غلط
// می‌دهد.
import type { SankeyLink, SankeyNode } from './sankey.ts';

/**
 * زیرمجموعه‌ای از `CrawlerFunnel` که نمودار لازم دارد.
 *
 * ⚠ همه‌اش از خانواده‌ی **وضعیت** است و هیچ `win_*`ی این‌جا نیست. عمدی است:
 * یک ستونِ پنجره‌ای کنارِ پنج ستونِ لحظه‌ای، نموداری می‌سازد که نصفش یک
 * سوال را جواب می‌دهد و نصفش سوالِ دیگری را.
 */
export interface FunnelCounts {
  candidates: number;
  cand_no_mention: number;
  mentions: number;
  clusters: number;
  policy_publish: number;
  policy_review: number;
  policy_reject: number;
  publishable_mapped: number;
}

export interface Flow { nodes: SankeyNode[]; links: SankeyLink[] }

/** منفی و NaN به صفر — یک عددِ منفی کلِ چیدمان را به‌هم می‌ریزد. */
const n0 = (n: number | undefined | null): number =>
  (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : 0);

/**
 * از شمارش‌ها یک نمودارِ لایه‌ای می‌سازد.
 *
 * چهار ستون، و هر ستون یک سوال:
 *   ۰ چند تکه‌ی رویدادمانند داریم؟  (Detect)
 *   ۱ چند تایش ادعای یک رویداد شد؟ (Claim + Mention — و دو ریزشش)
 *   ۲ چند رویدادِ یکتا؟             (Match + Cluster — و ادغام)
 *   ۳ سیاست چه گفت، و چقدرش رسید؟  (Publish Policy + Publisher)
 *
 * ⚠ **نمودار از «کاندیدا» شروع می‌شود و نه از «سند»، و این تعمیرِ یک
 * دروغِ خاموش است.** مقدارِ هر گره در سنکی بیشترِ «جمعِ ورودی» و «جمعِ
 * خروجی» است. گرهِ سند ورودی ندارد، پس عددش جمعِ خروجی‌اش می‌شد: «۲۹٬۹۹۳
 * کاندیدا + ۱۷۴ سندِ بی‌آیتم = ۳۰٬۱۶۷» — عددی که کنارِ برچسبِ «سندِ خام»
 * می‌نشست در حالی که سندها ۴٬۵۷۴ تا بودند. هر گره‌ای که واحدِ ورودی و
 * خروجی‌اش یکی نباشد همین را می‌کند.
 *
 * از کاندیدا به بعد همه‌ی گره‌ها **دقیقاً** جمع می‌خوانند. شمارشِ سند و
 * صفحه‌ی بی‌آیتم جای دیگری در صفحه است، به‌شکلِ عدد و نمونه.
 *
 * ریزش‌ها `terminal` علامت می‌خورند تا رنگشان فرق کند. چیزی که آدم در یک
 * نگاه می‌خواهد بداند این است که «کجا ریخت»، نه «چقدر رد شد».
 */
/**
 * مقدارِ هر گره، از شمارشِ خام. جدا از ساختِ نمودار، چون **دو بار** صدا
 * زده می‌شود: یک بار برای ستونِ ده روز و یک بار برای آخرین اجرا.
 *
 * ⚠ در این گراف مقدارِ هر جریان دقیقاً مقدارِ گرهِ مقصدش است (هر گره یک
 * ورودی دارد و بس). پس همین یک جدول هم گره‌ها را می‌سازد و هم جریان‌ها،
 * و امکانِ اینکه این دو از هم فاصله بگیرند اصلاً وجود ندارد.
 */
function funnelValues(f: FunnelCounts): Record<string, number> {
  const candidates = n0(f.candidates);
  const mention = n0(f.mentions);
  const nomention = Math.min(n0(f.cand_no_mention), candidates);
  // ⚠ تفریق، نه یک شمارشِ جدا. کاندیدایی که نه mention شده و نه حکمِ
  // «فیلدِ ناکافی» گرفته، هنوز به مرحله‌ی ساختِ mention نرسیده — صفِ کار
  // است، نه نشتی. با شمارشِ مستقیم، ردیف‌های سه جدولِ برآیند (که به‌ازای
  // هر **اجرا** ردیف دارند، نه هر کاندیدا) دوباره‌شماری می‌شد و جمعِ
  // ستون از خودش بزرگ‌تر می‌شد.
  const pending = Math.max(candidates - mention - nomention, 0);

  const cluster = n0(f.clusters);
  // هر mention یا خوشه‌ی خودش را ساخت یا با یکی دیگر یکی شد.
  const merged = Math.max(mention - cluster, 0);

  const publishable = n0(f.policy_publish);
  const inapp = Math.min(n0(f.publishable_mapped), publishable);

  return {
    candidate: candidates,
    mention,
    nomention,
    pending,
    cluster,
    merged,
    publishable,
    review: n0(f.policy_review),
    rejected: n0(f.policy_reject),
    inapp,
    // «حکمش publish است ولی هنوز نگاشتی در اپ ندارد.»
    notsent: Math.max(publishable - inapp, 0),
  };
}

const FUNNEL_NODES: SankeyNode[] = [
  {
    id: 'candidate', label: 'کاندیدا', layer: 0,
    why: 'تکه‌ای از یک صفحه که کرالر آن را یک رویدادِ محتمل شمرده. هنوز هیچ '
      + 'فیلدی از آن درنیامده — فقط «این‌جا شاید یک رویداد باشد».',
  },
  {
    id: 'mention', label: 'mention', layer: 1,
    why: 'کاندیدایی که استخراج از آن دستِ‌کم عنوان و تاریخ درآورد؛ یعنی حالا '
      + 'می‌شود گفت یک رویداد است. هنوز یکتا نیست — همین رویداد از منبعِ '
      + 'دیگر mentionِ جدایی دارد.',
  },
  {
    id: 'nomention', label: 'فیلدِ ناکافی', layer: 1, terminal: true,
    why: 'ادعا داشت ولی نه آن‌قدر که بشود گفت یک رویداد است (عنوان + تاریخ). '
      + 'بزرگ‌ترین ریزشِ کلِ خط.',
  },
  {
    id: 'pending', label: 'در صفِ کار', layer: 1, terminal: true,
    why: 'نه mention شد و نه حکمِ «فیلدِ ناکافی» گرفت — یعنی هنوز نوبتش '
      + 'نرسیده. این عدد از تفریق در می‌آید، نه از شمارشِ مستقیم.',
  },
  {
    id: 'cluster', label: 'خوشه', layer: 2,
    why: 'یک رویدادِ **یکتا**. چند mention که matcher گفت یکی‌اند، یک خوشه '
      + 'می‌شوند. شمارشش به نسلِ خوشه‌بندی بند است، نه به پنجره‌ی زمانی.',
  },
  {
    id: 'merged', label: 'ادغام‌شده', layer: 2, terminal: true,
    why: 'mentionی که با یکی دیگر جفت شد و خوشه‌ی جدا نساخت. ریزش نیست — '
      + 'همان رویداد است که از منبعِ دوم هم دیده شده.',
  },
  {
    id: 'publishable', label: 'منتشرشدنی', layer: 3,
    why: 'سیاستِ انتشار گفت «آری». یعنی «می‌تواند برود»، نه «رفته».',
  },
  {
    id: 'review', label: 'بازبینی', layer: 3, terminal: true,
    why: 'سیاست نه «آری» گفت نه «نه». تا وقتی این‌جاست به اپ نمی‌رود؛ صفِ '
      + 'بازبینیِ ادمین همین‌هاست.',
  },
  {
    id: 'rejected', label: 'ردشده', layer: 3, terminal: true,
    why: 'سیاست ردش کرد — معمولاً لینکِ رقیب یا منبعِ خاموش.',
  },
  {
    id: 'inapp', label: 'در اپ', layer: 4,
    why: 'به `public.events` رسیده و کاربر می‌تواند ببیندش. تهِ خط.',
  },
  {
    id: 'notsent', label: 'نرفته', layer: 4, terminal: true,
    why: 'حکمش publish است ولی هنوز نگاشتی در اپ ندارد — یعنی publisher '
      + 'هنوز نبردتش.',
  },
];

const FUNNEL_EDGES: { from: string; to: string; label?: string }[] = [
  { from: 'candidate', to: 'mention', label: 'Mention' },
  { from: 'candidate', to: 'nomention', label: 'ادعای ناکافی' },
  { from: 'candidate', to: 'pending' },

  { from: 'mention', to: 'cluster', label: 'Cluster' },
  { from: 'mention', to: 'merged', label: 'Match' },

  { from: 'cluster', to: 'publishable', label: 'publish' },
  { from: 'cluster', to: 'review', label: 'review' },
  { from: 'cluster', to: 'rejected', label: 'reject' },

  { from: 'publishable', to: 'inapp', label: 'Publisher' },
  { from: 'publishable', to: 'notsent' },
];

/**
 * گره‌ها و جریان‌ها را از یک جدولِ مقدار می‌سازد.
 *
 * ⚠ `run` فقط روی گره می‌نشیند و نه روی جریان: ضخامتِ جریان از ستونِ ده روز
 * می‌آید و اگر عددِ دوم هم شکل عوض می‌کرد، دو نمودار روی هم کشیده می‌شد.
 */
function build(
  nodes: SankeyNode[],
  edges: { from: string; to: string; label?: string }[],
  v: Record<string, number>,
  run?: Record<string, number>,
): Flow {
  return {
    nodes: run ? nodes.map((n) => ({ ...n, run: run[n.id] ?? 0 })) : nodes,
    // جریانِ صفر کشیده نمی‌شود ولی گره‌اش می‌ماند: نبودنِ «صفحه‌ی بی‌آیتم» در
    // نمودار خودش یک خبرِ خوب است و باید دیده شود، نه اینکه گره‌اش غیب شود.
    links: edges
      .map((e) => ({ ...e, value: v[e.to] ?? 0 }))
      .filter((l) => l.value > 0),
  };
}

/**
 * از شمارش‌ها یک نمودارِ لایه‌ای می‌سازد.
 *
 * چهار ستون، و هر ستون یک سوال:
 *   ۰ چند تکه‌ی رویدادمانند داریم؟  (Detect)
 *   ۱ چند تایش ادعای یک رویداد شد؟ (Claim + Mention — و دو ریزشش)
 *   ۲ چند رویدادِ یکتا؟             (Match + Cluster — و ادغام)
 *   ۳ سیاست چه گفت، و چقدرش رسید؟  (Publish Policy + Publisher)
 *
 * `run` اختیاری است و عددِ دومِ هر گره را می‌دهد — همان «آخرین اجرا»یی که
 * بقیه‌ی صفحه کنارِ هر عدد می‌گذارد. بدونش نمودار همان یک‌عددیِ قبلی است.
 *
 * ⚠ **نمودار از «کاندیدا» شروع می‌شود و نه از «سند»، و این تعمیرِ یک
 * دروغِ خاموش است.** مقدارِ هر گره در سنکی بیشترِ «جمعِ ورودی» و «جمعِ
 * خروجی» است. گرهِ سند ورودی ندارد، پس عددش جمعِ خروجی‌اش می‌شد: «۲۹٬۹۹۳
 * کاندیدا + ۱۷۴ سندِ بی‌آیتم = ۳۰٬۱۶۷» — عددی که کنارِ برچسبِ «سندِ خام»
 * می‌نشست در حالی که سندها ۴٬۵۷۴ تا بودند. هر گره‌ای که واحدِ ورودی و
 * خروجی‌اش یکی نباشد همین را می‌کند.
 *
 * از کاندیدا به بعد همه‌ی گره‌ها **دقیقاً** جمع می‌خوانند. شمارشِ سند و
 * صفحه‌ی بی‌آیتم جای دیگری در صفحه است، به‌شکلِ عدد و نمونه.
 *
 * ریزش‌ها `terminal` علامت می‌خورند تا رنگشان فرق کند. چیزی که آدم در یک
 * نگاه می‌خواهد بداند این است که «کجا ریخت»، نه «چقدر رد شد».
 */
export function funnelToFlow(f: FunnelCounts, run?: FunnelCounts): Flow {
  return build(
    FUNNEL_NODES, FUNNEL_EDGES,
    funnelValues(f), run ? funnelValues(run) : undefined,
  );
}

/** ریزش‌های بی‌سر و صدا، برای نوارِ هشدارِ بالای نمودار. */
export function flowWarnings(f: Partial<FunnelCounts> & {
  detect_barren?: number;
  cand_claims_none?: number;
  events_dead_cluster?: number;
  events_no_date?: number;
  events_no_url?: number;
  win_fetch_blocked?: number;
  win_fetch_errors?: number;
}): { key: string; value: number }[] {
  return ([
    ['win_fetch_errors', n0(f.win_fetch_errors)],
    ['win_fetch_blocked', n0(f.win_fetch_blocked)],
    ['detect_barren', n0(f.detect_barren)],
    ['cand_claims_none', n0(f.cand_claims_none)],
    ['events_dead_cluster', n0(f.events_dead_cluster)],
    ['events_no_date', n0(f.events_no_date)],
    ['events_no_url', n0(f.events_no_url)],
  ] as const)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({ key, value }));
}

// ═══════════════════════════════════════════════════════════════════════
// نمودارِ دوم: سرنوشتِ درخواست‌های صفحه
// ═══════════════════════════════════════════════════════════════════════

/**
 * شمارشِ `crawler.fetch_events` — واحدش **یک آدرس**، نه یک رویداد.
 *
 * ⚠ این‌ها عمداً با `FunnelCounts` قاطی نشده‌اند و نمودارشان هم جداست.
 * واحدشان یکی نیست: آن‌جا «یک رویدادِ نامزد» است و این‌جا «یک درخواستِ
 * HTTP». یک صفحه‌ی فهرست می‌تواند سی کاندیدا بدهد و یک کاندیدا می‌تواند
 * هیچ درخواستی نداشته باشد. کشیدنشان در یک نمودار همان اشتباهی است که
 * سرِ `funnelToFlow` توضیح داده شده، فقط یک پله بالاتر.
 */
export interface FetchCounts {
  fetch_requests: number;
  fetch_ok: number;
  fetch_unchanged: number;
  fetch_errors: number;
  fetch_blocked: number;
  fetch_skipped: number;
  fetch_blocked_robots: number;
  fetch_blocked_tier: number;
  fetch_blocked_disabled: number;
}

/**
 * از شمارشِ درخواست‌ها یک نمودارِ سه‌ستونی می‌سازد.
 *
 * سه ستون، و ستونِ میانی جوابِ سوالی است که این نمودار برایش ساخته شد:
 *   ۰ چند آدرس در صف بود؟
 *   ۱ **اصلاً به شبکه زده شد یا نه؟**
 *   ۲ و آن‌جا چه شد / چرا نرفت؟
 *
 * ⚠ ستونِ میانی روی داده‌ی واقعی برعکسِ شهود است: از ۳۵ هزار درخواستِ ده
 * روز، ۱۵ هزارش هرگز به شبکه نرفت — ۱۱ هزار «کشِ لینکِ مرده» (آدرسی که سه
 * بار خطا داده و هفت روز کنار گذاشته شده) و بقیه مسدود. تا وقتی این ستون
 * نبود، آدم آن یازده هزار را «حتماً کش بوده» می‌خواند، در حالی که کشِ
 * واقعی — یعنی «رفتیم و سرور گفت عوض نشده» — فقط ۱٬۲۰۵ تا بود.
 *
 * ⚠ **جمع‌ها دقیق‌اند و باید بمانند.** ستونِ `outcome` یک CHECK با پنج
 * مقدار است، پس پنج نتیجه دقیقاً `fetch_requests` می‌شوند. سه دلیلِ
 * «مسدود» ولی تضمینی ندارند — کرالر می‌تواند دلیلِ تازه‌ای بنویسد — پس
 * باقی‌مانده‌شان به‌شکلِ «مسدودِ دیگر» کشیده می‌شود و بی‌صدا گم نمی‌شود.
 */
function fetchValues(f: FetchCounts): Record<string, number> {
  const ok = n0(f.fetch_ok);
  const unchanged = n0(f.fetch_unchanged);
  const error = n0(f.fetch_errors);
  const blocked = n0(f.fetch_blocked);
  const skipped = n0(f.fetch_skipped);

  const robots = Math.min(n0(f.fetch_blocked_robots), blocked);
  const tier = Math.min(n0(f.fetch_blocked_tier), blocked - robots);
  const disabled = Math.min(n0(f.fetch_blocked_disabled), blocked - robots - tier);

  return {
    // ⚠ گرهِ ریشه هم این‌جا مقدار می‌گیرد، وگرنه عددِ دومش صفر می‌ماند:
    // ارتفاعِ ریشه را چیدمان از جمعِ خروجی‌هایش حساب می‌کند و مقدارِ صریح
    // لازم ندارد، ولی «آخرین اجرا» یک عددِ نوشتنی است و از این جدول می‌آید.
    // (روی داده‌ی واقعی دیده شد: «۳۵٬۱۷۸ · ۰» بالای نموداری که ۳٬۵۰۶
    // درخواستِ همان اجرا را می‌کشید.)
    //
    // از جمعِ سطل‌ها و نه از `fetch_requests`: همان عددی که واقعاً کشیده
    // می‌شود. اگر روزی مقدارِ ششمی به `outcome` اضافه شود، این دو از هم
    // فاصله می‌گیرند و گره باید همان را بگوید که تجزیه کرده.
    request: ok + unchanged + error + skipped + blocked,
    net: ok + unchanged + error,
    nonet: skipped + blocked,
    ok,
    unchanged,
    error,
    skipped,
    robots,
    tier,
    disabled,
    blocked_other: Math.max(blocked - robots - tier - disabled, 0),
  };
}

const FETCH_NODES: SankeyNode[] = [
  {
    id: 'request', label: 'درخواستِ صفحه', layer: 0,
    why: 'هر آدرسی که کرالر در این پنجره خواست بگیرد — یک ردیف در '
      + '`crawler.fetch_events` به ازای هر **آدرس**، نه هر منبع. برای همین '
      + 'از شمارِ «اجرا» در جدولِ فازها ده‌ها برابر بزرگ‌تر است.',
  },
  {
    id: 'net', label: 'به شبکه رفت', layer: 1,
    why: 'واقعاً یک درخواستِ HTTP زده شد و جوابی آمد — چه صفحه، چه ۳۰۴، چه خطا.',
  },
  {
    id: 'nonet', label: 'اصلاً نرفت', layer: 1,
    why: 'کرالر پیش از زدن به شبکه خودش جلوی خودش را گرفت. این‌ها هزینه‌ی '
      + 'شبکه ندارند، ولی شمرده می‌شوند — وگرنه عددِ بالا با جمعِ زیرش نمی‌خواند.',
  },
  {
    id: 'ok', label: 'گرفته شد', layer: 2,
    why: 'صفحه گرفته شد و بدنه‌اش سندِ خام شد. از این‌جا به بعد کارِ نمودارِ '
      + 'پایین است.',
  },
  {
    id: 'unchanged', label: 'عوض نشده بود', layer: 2,
    why: 'رفتیم و سرور گفت از دفعه‌ی قبل عوض نشده (۳۰۴، یا همان محتوا). '
      + '**این کشِ واقعی است** — و با «کشِ لینکِ مرده» یکی نیست.',
  },
  {
    id: 'error', label: 'خطا', layer: 2, terminal: true,
    why: 'صفحه اصلاً گرفته نشد — خطای شبکه، تایم‌اوت، یا ۴۰۰ به بالا. سه بارِ '
      + 'پیاپیِ این، آدرس را به «کشِ لینکِ مرده» می‌فرستد.',
  },
  {
    id: 'skipped', label: 'کشِ لینکِ مرده', layer: 2, terminal: true,
    why: 'آدرسی که سه بار پشتِ سرِ هم خطا داده؛ کرالر تا هفت روز سراغش '
      + 'نمی‌رود. بزرگ‌ترین سطلِ این نمودار، و تا مایگریشنِ ۰۱۱۵ هیچ‌جای '
      + 'صفحه شمرده نمی‌شد.',
  },
  {
    id: 'robots', label: 'robots.txt', layer: 2, terminal: true,
    why: '`robots.txt`ِ خودِ سایت این مسیر را ممنوع کرده. کارِ ما نیست — یا '
      + 'منبع باید کنار گذاشته شود یا مسیرِ دیگری از همان سایت.',
  },
  {
    id: 'tier', label: 'حکمِ دسترسی', layer: 2, terminal: true,
    why: 'سیاستِ دسترسیِ **خودمان** اجازه نداد (ستونِ `tier`ِ منبع در رجیستری). '
      + 'برخلافِ robots، این یکی با یک ویرایش در «منابع» عوض می‌شود.',
  },
  {
    id: 'disabled', label: 'منبعِ خاموش', layer: 2, terminal: true,
    why: 'منبع در رجیستری خاموش است، ولی آدرس‌هایش هنوز در صف می‌آیند و سرِ '
      + 'صف رد می‌شوند. بی‌ضرر است، ولی بی‌فایده هم هست.',
  },
];

const FETCH_EDGES: { from: string; to: string; label?: string }[] = [
  { from: 'request', to: 'net' },
  { from: 'request', to: 'nonet' },

  { from: 'net', to: 'ok', label: 'Fetch' },
  { from: 'net', to: 'unchanged', label: '304' },
  { from: 'net', to: 'error' },

  { from: 'nonet', to: 'skipped', label: 'کشِ خطا' },
  { from: 'nonet', to: 'robots' },
  { from: 'nonet', to: 'tier' },
  { from: 'nonet', to: 'disabled' },
  { from: 'nonet', to: 'blocked_other' },
];

const BLOCKED_OTHER: SankeyNode = {
  id: 'blocked_other', label: 'مسدودِ دیگر', layer: 2, terminal: true,
  why: 'مسدود، با دلیلی که این نمودار هنوز اسمی برایش ندارد. اگر این گره '
    + 'دیده شد یعنی کرالر دلیلِ تازه‌ای می‌نویسد و باید به تاکسونومی اضافه شود.',
};

/**
 * از شمارشِ درخواست‌ها یک نمودارِ سه‌ستونی می‌سازد.
 *
 * سه ستون، و ستونِ میانی جوابِ سوالی است که این نمودار برایش ساخته شد:
 *   ۰ چند آدرس در صف بود؟
 *   ۱ **اصلاً به شبکه زده شد یا نه؟**
 *   ۲ و آن‌جا چه شد / چرا نرفت؟
 *
 * ⚠ ستونِ میانی روی داده‌ی واقعی برعکسِ شهود است: از ۳۵ هزار درخواستِ ده
 * روز، ۱۵ هزارش هرگز به شبکه نرفت — ۱۱ هزار «کشِ لینکِ مرده» (آدرسی که سه
 * بار خطا داده و هفت روز کنار گذاشته شده) و بقیه مسدود. تا وقتی این ستون
 * نبود، آدم آن یازده هزار را «حتماً کش بوده» می‌خواند، در حالی که کشِ
 * واقعی — یعنی «رفتیم و سرور گفت عوض نشده» — فقط ۱٬۲۰۵ تا بود.
 *
 * ⚠ **جمع‌ها دقیق‌اند و باید بمانند.** ستونِ `outcome` یک CHECK با پنج
 * مقدار است، پس پنج نتیجه دقیقاً `fetch_requests` می‌شوند. سه دلیلِ
 * «مسدود» ولی تضمینی ندارند — کرالر می‌تواند دلیلِ تازه‌ای بنویسد — پس
 * باقی‌مانده‌شان به‌شکلِ «مسدودِ دیگر» کشیده می‌شود و بی‌صدا گم نمی‌شود.
 */
export function fetchToFlow(f: FetchCounts, run?: FetchCounts): Flow {
  const v = fetchValues(f);
  const r = run ? fetchValues(run) : undefined;

  // ⚠ بر خلافِ بقیه‌ی گره‌ها، این یکی با صفر بودن **می‌افتد** — و فقط همین
  // یکی. جای دیگر صفر خبر است («هیچ صفحه‌ی بی‌آیتمی نداشتیم»)، این‌جا نیست:
  // این گره یک باقی‌مانده‌ی محافظتی است، نه یک دسته. صفر بودنش یعنی
  // تاکسونومی کامل است و کشیدنش فقط یک نوارِ دو پیکسلی می‌سازد که برچسبش
  // از پایینِ کادر می‌زند بیرون.
  //
  // ⚠ «صفر» یعنی در **هر دو** ستون صفر: اگر ده‌روزه صفر باشد ولی آخرین اجرا
  // نه، حذفِ گره دقیقاً همان چیزی را پنهان می‌کند که تازه پیدا شده.
  const nodes = v.blocked_other > 0 || (r?.blocked_other ?? 0) > 0
    ? [...FETCH_NODES, BLOCKED_OTHER]
    : FETCH_NODES;

  return build(nodes, FETCH_EDGES, v, r);
}
